// FleetOverview.tsx — the console's Overview: a FLEET HEALTH dashboard.
//
// Bound to GET /api/platform/health/platform, which already returns everything
// this screen needs: serverStats, per-tenant rows, and fleet totals.
//
// This replaces the Phase A screen that occupied "Overview" — that one showed AI
// spend, calls and error rate, which is the design's USAGE & SPEND screen and has
// moved there.
//
// WHAT THIS SCREEN DELIBERATELY DOES NOT DO: invent a trend. `health/platform`
// returns point-in-time totals, not a 30-day series. The design's "Platform
// activity" chart wants requests and contracts per day; the endpoint has no such
// series and there is no rollup behind it. Rather than plot a fabricated curve
// from a single number — which would look exactly like real history — the chart
// area says what is missing. Everything else on this screen is live.

import React, { useCallback, useEffect, useState } from "react";
import { PT, PT_NUM, fmtNum } from "../../platformTheme";
import { pget, perr } from "./api";
import { PCard, PSection, PBtn, PBadge, PBanner, PSpinner, PTable, PRow, PId, th, td, tdNum, statusTone } from "./ui";
import { AreaChart, Donut, ProgressBar, Sparkline, NotCaptured } from "./charts";
import type { Tenant } from "./Customers";

/**
 * The REAL shape of utils/serverStats.computeServerStats(). Typed rather than
 * `Record<string, any>`, because the untyped version is what let this screen
 * ship reading `ss.cpu` as a number (it is an object), `ss.uptime` as a string
 * (it is `{seconds, formatted}`) and `ss.node` (it does not exist — the value is
 * `process.node_version`). tsc accepted all three; only the rendered page showed
 * the em dashes and the "[object Object]".
 */
interface ServerStats {
  cpu?: { usage_percent?: number; load_1m?: number; cores?: number };
  memory?: { usage_percent?: number; used_mb?: number; total_mb?: number };
  swap?: { usage_percent?: number };
  disk?: { total?: string; used?: string; available?: string; percent?: string };
  uptime?: { seconds?: number; formatted?: string };
  process?: { node_version?: string; rss_mb?: number };
  postgres?: { size?: string; connections?: number };
  error?: string;
}

/**
 * GET /health/platform/activity. `tenants_failed` is carried through to the
 * screen rather than swallowed: a series summed over only the tenants that
 * happened to read is a smooth, plausible, wrong curve, and the whole reason
 * this panel was withheld in the first place was refusing to draw one of those.
 */
interface Activity {
  days?: number;
  series?: { date: string; requests: number; contracts: number }[];
  tenants_failed?: number;
  error?: string;
}

interface PlatformHealth {
  serverStats?: ServerStats;
  tenants?: { tenant_id: string; name?: string; status?: string; users?: number; requests?: number; contracts?: number; error?: string }[];
  totals?: {
    tenants?: number; users?: number; logins7d?: number; requests?: number; contracts?: number; aiFields?: number;
    imports30d?: { total?: number; success?: number; failed?: number };
    extraction30dContracts?: number; inbound24h?: number;
  };
}

const kpiCard: React.CSSProperties = {
  background: PT.bg2, border: `1px solid ${PT.brd}`, borderRadius: PT.radiusLg,
  padding: 13, position: "relative", overflow: "hidden",
};

const Kpi: React.FC<{
  label: string; value: React.ReactNode; sub?: React.ReactNode; subTone?: "ok" | "warn" | "err" | "muted";
  accent: string; spark?: number[]; onClick?: () => void;
}> = ({ label, value, sub, subTone = "muted", accent, spark, onClick }) => {
  const [hov, setHov] = useState(false);
  const subColor = subTone === "ok" ? PT.okTx : subTone === "warn" ? PT.warnTx : subTone === "err" ? PT.errTx : PT.txD;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        ...kpiCard,
        cursor: onClick ? "pointer" : "default",
        borderColor: hov && onClick ? PT.brdStrong : PT.brd,
        boxShadow: hov && onClick ? "0 2px 6px rgba(16,24,40,.08)" : "0 1px 2px rgba(16,24,40,.04)",
      }}
    >
      {/* status dot — the design's coloured KPI accent */}
      <span style={{
        position: "absolute", top: 13, right: 13, width: 7, height: 7,
        borderRadius: 4, background: accent,
      }} />
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.55, textTransform: "uppercase", color: PT.txD }}>
        {label}
      </div>
      <div style={{ ...PT_NUM, fontSize: 24, fontWeight: 600, margin: "5px 0 3px", color: PT.tx }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: subColor }}>{sub}</div>}
      {spark && spark.length > 1 && (
        <div style={{ marginTop: 7 }}><Sparkline points={spark} color={accent} w={110} h={22} fill /></div>
      )}
    </div>
  );
};

export const FleetOverview: React.FC<{
  onOpenCustomer: (t: Tenant) => void;
  onGoUsage: () => void;
  onGoCustomers: () => void;
}> = ({ onOpenCustomer, onGoUsage, onGoCustomers }) => {
  const [h, setH] = useState<PlatformHealth | null>(null);
  const [act, setAct] = useState<Activity | null>(null);
  const [err, setErr] = useState("");
  const [at, setAt] = useState<Date | null>(null);

  // AI spend was a HARDCODED em dash captioned "rollup flag off" — an assertion
  // with a date on it and no re-check, which is CLAUDE.md §7.3 exactly. The flag
  // has since been on for weeks on dev and the tile still said it was off. It
  // now reads the same endpoint Usage & Spend does and reports what it finds.
  const [spend, setSpend] = useState<{ est_cost: number; calls: number } | null | "error">(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr(""); setBusy(true);
    // The activity series and the spend total are SEPARATE fetches on purpose:
    // each iterates every tenant, and a slow or failing one must not blank the
    // KPI row. Their failures render in their own places.
    const [r, a, s] = await Promise.all([
      pget("/health/platform"),
      pget("/health/platform/activity", { days: 30 }),
      pget("/usage/spend", { days: 30 }),
    ]);
    setBusy(false);
    if (!r.ok) { setErr(perr(r, "Could not load fleet health")); setH({}); return; }
    setH(r.data || {});
    setAct(a.ok ? (a.data || null) : { error: perr(a, "Could not load the activity series") });
    setSpend(s.ok ? { est_cost: Number(s.data?.totals?.est_cost || 0), calls: Number(s.data?.totals?.calls || 0) } : "error");
    setAt(new Date());
  }, []);

  useEffect(() => { load(); }, [load]);
  // The design's Overview copy says "auto-refresh 60s".
  useEffect(() => {
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  if (err) return <PBanner>{err}</PBanner>;
  if (!h) return <PSpinner label="Loading fleet health…" />;

  const t = h.totals || {};
  const rows = (h.tenants || []).filter((x) => !x.error);
  const broken = (h.tenants || []).filter((x) => x.error);
  const byStatus = (s: string) => rows.filter((x) => (x.status || "").toLowerCase() === s).length;
  const active = byStatus("active"), suspended = byStatus("suspended"), offboarded = byStatus("offboarded");
  const imports = t.imports30d || {};
  const failed = Number(imports.failed || 0);

  const ss: ServerStats = h.serverStats || {};
  // `disk.percent` arrives as a df string like "43%" — parse, don't assume a number.
  const pctOf = (v: unknown): number | null => {
    const n = typeof v === "string" ? Number(v.replace("%", "")) : Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const cpuPct = pctOf(ss.cpu?.usage_percent);
  const memPct = pctOf(ss.memory?.usage_percent);
  const diskPct = pctOf(ss.disk?.percent);
  const serverTiles: { label: string; value: string; sub?: string; pct: number | null }[] = [
    { label: "CPU", value: cpuPct == null ? "—" : `${cpuPct}%`, sub: ss.cpu?.cores ? `${ss.cpu.cores} cores · load ${ss.cpu.load_1m ?? "—"}` : undefined, pct: cpuPct },
    { label: "Memory", value: memPct == null ? "—" : `${memPct}%`, sub: ss.memory?.total_mb ? `${fmtNum(ss.memory.used_mb)} / ${fmtNum(ss.memory.total_mb)} MB` : undefined, pct: memPct },
    { label: "Disk", value: ss.disk?.percent || "—", sub: ss.disk?.used ? `${ss.disk.used} of ${ss.disk.total}` : undefined, pct: diskPct },
    { label: "Uptime", value: ss.uptime?.formatted || "—", pct: null },
    { label: "PostgreSQL", value: ss.postgres?.size || "—", sub: ss.postgres?.connections != null ? `${ss.postgres.connections} connections` : undefined, pct: null },
    { label: "Node.js", value: ss.process?.node_version || "—", sub: ss.process?.rss_mb != null ? `${fmtNum(ss.process.rss_mb)} MB rss` : undefined, pct: null },
  ];

  // Needs attention — every entry is derived from data on this response, never
  // invented. An empty list is stated as such rather than hidden.
  const attention: { title: string; sub: string; color: string; go: () => void }[] = [];
  if (suspended > 0) attention.push({ title: `${suspended} suspended customer${suspended > 1 ? "s" : ""}`, sub: "Sign-in blocked; data retained", color: PT.warn, go: onGoCustomers });
  if (failed > 0) attention.push({ title: `${failed} failed import${failed > 1 ? "s" : ""} in 30 days`, sub: `of ${fmtNum(imports.total)} attempted`, color: PT.err, go: onGoCustomers });
  if (broken.length) attention.push({ title: `${broken.length} customer${broken.length > 1 ? "s" : ""} failed the health probe`, sub: broken.map((b) => b.name || b.tenant_id).join(", ").slice(0, 90), color: PT.err, go: onGoCustomers });
  if (offboarded > 0) attention.push({ title: `${offboarded} offboarded customer${offboarded > 1 ? "s" : ""}`, sub: "Retained, read-only", color: PT.neutral, go: onGoCustomers });
  if (ss.error) attention.push({ title: "Server metrics unavailable", sub: String(ss.error).slice(0, 90), color: PT.err, go: () => {} });

  return (
    <>
      <PSection
        title="Fleet"
        sub={at ? `updated ${at.toLocaleTimeString()} · auto-refresh 60s` : undefined}
        right={<PBtn small disabled={busy} onClick={load}>{busy ? "Refreshing…" : "Refresh"}</PBtn>}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(196px, 1fr))", gap: 12 }}>
          <Kpi label="Customers" value={fmtNum(t.tenants)} accent={PT.chBlue} onClick={onGoCustomers}
               sub={`${active} active · ${suspended} susp · ${offboarded} off`} />
          <Kpi label="Users" value={fmtNum(t.users)} accent={PT.chCyan} onClick={onGoCustomers}
               sub={`${fmtNum(t.logins7d)} logins · 7d`} />
          <Kpi label="Requests" value={fmtNum(t.requests)} accent={PT.chBlue} onClick={onGoUsage} sub="all customers" />
          <Kpi label="Contracts" value={fmtNum(t.contracts)} accent={PT.chViolet} onClick={onGoUsage} sub="total managed" />
          <Kpi
            label="AI spend · 30d"
            value={
              spend === null ? "…"
              : spend === "error" ? <span style={{ color: PT.txD }}>—</span>
              : `$${spend.est_cost.toFixed(2)}`
            }
            accent={PT.chViolet}
            onClick={onGoUsage}
            // $0.00 with calls recorded is a PRICE gap, not a spend of nothing:
            // est_cost is 0 for any model with no row in ai_model_prices. Saying
            // which of the three states this is keeps a zero from reading as a fact.
            sub={
              spend === null ? "loading"
              : spend === "error" ? "could not read"
              : spend.calls === 0 ? "no calls recorded"
              : spend.est_cost === 0 ? `${fmtNum(spend.calls)} calls · no prices set`
              : `${fmtNum(spend.calls)} calls`
            }
            subTone={spend === "error" ? "err" : "muted"}
          />
          <Kpi label="AI fields" value={fmtNum(t.aiFields)} accent={PT.chCyan} onClick={onGoUsage} sub="extracted" />
          <Kpi label="Imports 30d" value={fmtNum(imports.total)} accent={PT.chAmber} onClick={onGoCustomers}
               sub={`${fmtNum(imports.success)} ok · ${fmtNum(imports.failed)} failed`}
               subTone={failed > 0 ? "err" : "muted"} />
          <Kpi label="Inbound 24h" value={fmtNum(t.inbound24h)} accent={PT.chBlue} onClick={onGoCustomers} sub="emails ingested" />
        </div>
      </PSection>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(260px, 1fr)", gap: 14, marginBottom: 22 }}>
        <PCard>
          <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 3 }}>Platform activity</div>
          <div style={{ fontSize: 11, color: PT.txD, marginBottom: 11 }}>Requests and contracts created · 30 days</div>
          {act?.error ? (
            <NotCaptured what="Daily request and contract history" needs={act.error} />
          ) : !act?.series ? (
            <PSpinner label="Loading activity…" />
          ) : (
            <>
              {/* A partial series is labelled ON the chart, not in a log line. An
                  operator reading a dip needs to know whether it is a real dip or
                  a customer that failed to read. */}
              {(act.tenants_failed || 0) > 0 && (
                <PBanner tone="warn">
                  {act.tenants_failed} customer{act.tenants_failed === 1 ? "" : "s"} could not be read —
                  this series is incomplete and the totals below it are not.
                </PBanner>
              )}
              <AreaChart
                height={170}
                labels={act.series.map((p) => p.date)}
                series={[
                  { label: "Requests created", color: PT.chBlue, values: act.series.map((p) => p.requests) },
                  { label: "Contracts created", color: PT.chViolet, values: act.series.map((p) => p.contracts), dashed: true },
                ]}
              />
            </>
          )}
        </PCard>

        <PCard>
          <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 3 }}>Customer status</div>
          <div style={{ fontSize: 11, color: PT.txD, marginBottom: 13 }}>
            {fmtNum(rows.length)} customers on the shared engine
          </div>
          <Donut
            centerLabel="customers"
            segments={[
              { label: "Active", value: active, color: PT.ok },
              { label: "Suspended", value: suspended, color: PT.warn },
              { label: "Offboarded", value: offboarded, color: PT.neutral },
            ]}
          />
        </PCard>
      </div>

      <PSection title="Server performance" sub={ss.error ? undefined : "platform host"}>
        <PCard>
          {ss.error ? (
            <PBanner>Server metrics unavailable: {String(ss.error)}</PBanner>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 16 }}>
              {serverTiles.map((s) => (
                <div key={s.label}>
                  <div style={{ fontSize: 10, color: PT.txD, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                    {s.label}
                  </div>
                  <div style={{ ...PT_NUM, fontWeight: 600, fontSize: 21, margin: "3px 0 4px" }}>{s.value}</div>
                  {s.sub && <div style={{ fontSize: 10.5, color: PT.txD, marginBottom: 5 }}>{s.sub}</div>}
                  <ProgressBar pct={s.pct} />
                </div>
              ))}
            </div>
          )}
        </PCard>
      </PSection>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(260px, 1fr)", gap: 14 }}>
        <PCard style={{ padding: 0 }}>
          <div style={{ padding: "13px 16px", borderBottom: `1px solid ${PT.brdSubtle}`, fontSize: 13.5, fontWeight: 600 }}>
            Per-customer snapshot
          </div>
          {rows.length === 0 ? (
            <div style={{ padding: 22, textAlign: "center", color: PT.txD, fontSize: 12.5 }}>No customers.</div>
          ) : (
            <PTable
              minWidth={640}
              head={<>
                <th style={th}>Customer</th><th style={th}>Status</th>
                <th style={{ ...th, textAlign: "right" }}>Users</th>
                <th style={{ ...th, textAlign: "right" }}>Requests</th>
                <th style={th}>Activity</th>
              </>}
            >
              {rows.map((r) => (
                <PRow
                  key={r.tenant_id}
                  onClick={() => onOpenCustomer({
                    tenant_id: r.tenant_id, name: r.name || r.tenant_id, status: r.status || "",
                    edition: "", created_at: "", user_count: r.users ?? 0,
                  })}
                >
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{r.name || "—"}</div>
                    <PId id={r.tenant_id} />
                  </td>
                  <td style={td}><PBadge tone={statusTone(r.status)}>{r.status || "—"}</PBadge></td>
                  <td style={tdNum}>{fmtNum(r.users)}</td>
                  <td style={tdNum}>{fmtNum(r.requests)}</td>
                  <td style={td}>
                    {/* No per-customer time series exists on this endpoint, so the
                        cell shows the em dash the formatter returns rather than a
                        shaped line that would imply history. */}
                    <Sparkline points={[]} />
                  </td>
                </PRow>
              ))}
            </PTable>
          )}
        </PCard>

        <PCard style={{ padding: 0 }}>
          <div style={{
            padding: "13px 16px", borderBottom: `1px solid ${PT.brdSubtle}`,
            display: "flex", alignItems: "center", gap: 9,
          }}>
            <span style={{ fontSize: 13.5, fontWeight: 600 }}>Needs attention</span>
            <span style={{
              fontSize: 11, fontWeight: 600, color: PT.warnTx, background: PT.warnSoft,
              padding: "2px 9px", borderRadius: 8,
            }}>
              {attention.length}
            </span>
          </div>
          <div style={{ padding: 13, display: "flex", flexDirection: "column", gap: 9 }}>
            {attention.length === 0 ? (
              <div style={{ fontSize: 12.5, color: PT.txD, textAlign: "center", padding: "14px 0" }}>
                Nothing needs attention.
              </div>
            ) : attention.map((a) => (
              <div
                key={a.title}
                onClick={a.go}
                style={{
                  display: "flex", alignItems: "center", gap: 11, padding: "10px 11px",
                  border: `1px solid ${PT.brdSubtle}`, borderRadius: 9, cursor: "pointer",
                }}
              >
                <span style={{ width: 7, height: 32, borderRadius: 4, background: a.color, flex: "none" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>{a.title}</div>
                  <div style={{ fontSize: 11, color: PT.txD, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {a.sub}
                  </div>
                </div>
                <span style={{ color: PT.txD, fontSize: 15 }}>›</span>
              </div>
            ))}
          </div>
        </PCard>
      </div>
    </>
  );
};
